import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Radio,
    Box,
    IconButton,
    Tabs,
    Tab,
    Link,
    ToggleButton,
} from "@mui/material";
import { CrfField, CrfFieldType, CrfReading, Laterality, CrfReadingValue } from "../nebula.service";
import DisplayText from "../../../components/DisplayText/DisplayText";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CompressIcon from "@mui/icons-material/Compress";
import { EditField } from "./EditComponents";
import { useAdjudicateReadingsMutation } from "../dataAccess";
import { useSnackbar } from "notistack";
import { hasAnyConflicts } from "../utils/conflictChecks";

interface AdjudicationDialogProps {
    open: boolean;
    onClose: () => void;
    laterality: Laterality | null;
    readings: CrfReading[];
    fields: CrfField[];
    patientId: string;
    visitId: string;
}

// Interface for values that need to be preserved per laterality
interface LateralityState {
    selectedValues: Record<string, string>;
    customValues: Record<string, string>;
}

const AdjudicationDialog: React.FC<AdjudicationDialogProps> = ({
    open,
    onClose,
    laterality,
    readings,
    fields,
    patientId,
    visitId,
}) => {
    const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
    const [customValues, setCustomValues] = useState<Record<string, string>>({});
    const [editMode, setEditMode] = useState<Record<string, boolean>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const { enqueueSnackbar } = useSnackbar();

    // Keep track of values before editing to restore them if canceled
    const [previousValues, setPreviousValues] = useState<Record<string, string>>({});

    // Track whether field values have been modified during editing
    const [isModified, setIsModified] = useState<Record<string, boolean>>({});

    // Tab handling
    const [activeTab, setActiveTab] = useState<Laterality>(Laterality.OD);

    // Density toggle - when true, rows are compact
    const [isCompactRows, setIsCompactRows] = useState(true);

    // Store state separately for each laterality
    const [lateralityState, setLateralityState] = useState<Record<Laterality, LateralityState>>({
        [Laterality.OD]: { selectedValues: {}, customValues: {} },
        [Laterality.OS]: { selectedValues: {}, customValues: {} },
    });

    const [adjudicateReadings] = useAdjudicateReadingsMutation();

    // Determine which lateralities need adjudication
    const canAdjudicate = React.useMemo(() => {
        const result = {
            [Laterality.OS]: false,
            [Laterality.OD]: false,
        };

        // For OS laterality
        const osReadings = readings.filter((reading) => reading.laterality === Laterality.OS);
        if (osReadings.length >= 2) {
            // Check if none are marked as final (no longer requiring conflicts)
            const hasFinal = osReadings.some((reading) => reading.final);
            result[Laterality.OS] = !hasFinal;
        }

        // For OD laterality
        const odReadings = readings.filter((reading) => reading.laterality === Laterality.OD);
        if (odReadings.length >= 2) {
            // Check if none are marked as final (no longer requiring conflicts)
            const hasFinal = odReadings.some((reading) => reading.final);
            result[Laterality.OD] = !hasFinal;
        }

        return result;
    }, [readings]);

    // Reset selected values when dialog opens with new readings and set initial active tab
    useEffect(() => {
        if (open && readings.length > 0) {
            // Start with empty selections for all lateralities
            setLateralityState({
                [Laterality.OD]: { selectedValues: {}, customValues: {} },
                [Laterality.OS]: { selectedValues: {}, customValues: {} },
            });

            // Reset current state
            setSelectedValues({});
            setCustomValues({});
            setEditMode({});
            setValidationErrors({});
            setPreviousValues({});
            setIsModified({});

            // Set active tab to the laterality that was initially requested
            // or the one that needs adjudication if the initial one doesn't
            if (laterality) {
                if (canAdjudicate[laterality]) {
                    setActiveTab(laterality);
                } else if (canAdjudicate[Laterality.OD]) {
                    setActiveTab(Laterality.OD);
                } else if (canAdjudicate[Laterality.OS]) {
                    setActiveTab(Laterality.OS);
                }
            } else if (canAdjudicate[Laterality.OD]) {
                setActiveTab(Laterality.OD);
            } else if (canAdjudicate[Laterality.OS]) {
                setActiveTab(Laterality.OS);
            }
        }
    }, [open, readings, canAdjudicate, laterality]);

    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newTab: Laterality) => {
        // Save current tab state before switching
        setLateralityState((prev) => ({
            ...prev,
            [activeTab]: {
                selectedValues,
                customValues,
            },
        }));

        // Load the state for the new tab
        setSelectedValues(lateralityState[newTab].selectedValues);
        setCustomValues(lateralityState[newTab].customValues);

        // Reset temporary states that shouldn't persist
        setEditMode({});
        setValidationErrors({});
        setPreviousValues({});
        setIsModified({});

        setActiveTab(newTab);
    };

    // Filter readings to only those matching the active tab laterality
    const filteredReadings = readings.filter((reading) => reading.laterality === activeTab);

    // Handle selection of a value for a field
    const handleSelectValue = (fieldName: string, value: string) => {
        setSelectedValues((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
        // Clear any custom value when selecting a predefined value
        setCustomValues((prev) => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
        // Clear any validation errors
        setValidationErrors((prev) => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
    };

    // Start edit mode for a field
    const startEditMode = (fieldName: string) => {
        // Save current value before editing
        const currentValue = customValues[fieldName] || selectedValues[fieldName] || "";
        setPreviousValues((prev) => ({
            ...prev,
            [fieldName]: currentValue,
        }));

        // Set edit mode and reset modification flag
        setEditMode((prev) => ({
            ...prev,
            [fieldName]: true,
        }));

        setIsModified((prev) => ({
            ...prev,
            [fieldName]: false,
        }));
    };

    // Cancel edit mode for a field
    const cancelEditMode = (fieldName: string) => {
        // Restore previous value
        const previousValue = previousValues[fieldName];

        if (previousValue) {
            // Determine if this was a custom value or selected value
            const wasCustom = !getUniqueValues(fieldName).some((v) => v.value === previousValue);

            if (wasCustom) {
                setCustomValues((prev) => ({
                    ...prev,
                    [fieldName]: previousValue,
                }));
                setSelectedValues((prev) => {
                    const updated = { ...prev };
                    delete updated[fieldName];
                    return updated;
                });
            } else {
                handleSelectValue(fieldName, previousValue);
            }
        } else {
            // If no previous value, clear both
            setCustomValues((prev) => {
                const updated = { ...prev };
                delete updated[fieldName];
                return updated;
            });
            setSelectedValues((prev) => {
                const updated = { ...prev };
                delete updated[fieldName];
                return updated;
            });
        }

        // Clear validation errors
        setValidationErrors((prev) => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });

        // Exit edit mode
        setEditMode((prev) => ({
            ...prev,
            [fieldName]: false,
        }));
    };

    // Confirm and accept edit changes
    const confirmEdit = (fieldName: string) => {
        // Only proceed if there are no validation errors
        if (validationErrors[fieldName]) return;

        // Exit edit mode
        setEditMode((prev) => ({
            ...prev,
            [fieldName]: false,
        }));

        // Clear modification flag
        setIsModified((prev) => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
    };

    // Handle custom value edit
    const handleCustomValueChange = (fieldName: string, value: string) => {
        const field = fields.find((f) => f.fieldName === fieldName);
        if (!field) return;

        // Basic validation
        let error = "";
        if (value) {
            switch (field.fieldType) {
                case CrfFieldType.float:
                    if (isNaN(Number(value))) {
                        error = "Please enter a valid number";
                    }
                    break;
                case CrfFieldType.date:
                    if (isNaN(Date.parse(value))) {
                        error = "Please enter a valid date";
                    }
                    break;
            }
        }

        // Track if the value is modified
        const originalValue = previousValues[fieldName] || "";
        setIsModified((prev) => ({
            ...prev,
            [fieldName]: value !== originalValue,
        }));

        // Update validation errors
        setValidationErrors((prev) => ({
            ...prev,
            [fieldName]: error,
        }));

        // Update custom value
        setCustomValues((prev) => ({
            ...prev,
            [fieldName]: value,
        }));

        // When setting a custom value, clear selection
        setSelectedValues((prev) => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
    };

    // Check if there is a value for a specific field
    const hasValue = (fieldName: string): boolean => {
        return getUniqueValues(fieldName).length > 0;
    };

    // Check if there's a conflict for a specific field
    const hasConflict = (fieldName: string): boolean => {
        const values = new Set();

        for (const reading of filteredReadings) {
            const fieldValue = reading.values.find((val) => val.fieldName === fieldName)?.fieldValue;
            if (fieldValue !== undefined) {
                values.add(fieldValue);
            }
        }

        return values.size > 1;
    };

    // Get all unique values for a field
    const getUniqueValues = (fieldName: string): { value: string; userEmail: string }[] => {
        const valueMap = new Map<string, string[]>();

        for (const reading of filteredReadings) {
            const fieldValue = reading.values.find((val) => val.fieldName === fieldName)?.fieldValue;
            if (fieldValue !== undefined) {
                if (!valueMap.has(fieldValue)) {
                    valueMap.set(fieldValue, []);
                }
                valueMap.get(fieldValue)!.push(reading.user.email);
            }
        }

        return Array.from(valueMap.entries()).map(([value, emails]) => ({
            value,
            userEmail: emails.join(", "),
        }));
    };

    // Handle submit
    const handleSubmit = async () => {
        // Before submitting, save the current state for the active tab
        setLateralityState((prev) => ({
            ...prev,
            [activeTab]: {
                selectedValues,
                customValues,
            },
        }));

        // Check for any validation errors first
        if (Object.values(validationErrors).some((error) => error)) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Combine selected values and custom values for submission
            const finalValues = { ...selectedValues };

            // Add custom values to the final values
            Object.entries(customValues).forEach(([fieldName, value]) => {
                finalValues[fieldName] = value;
            });

            // For fields with no conflict and no custom value, use the first/only value
            fields.forEach((field) => {
                if (!finalValues[field.fieldName]) {
                    const uniqueValues = getUniqueValues(field.fieldName);
                    if (uniqueValues.length > 0) {
                        finalValues[field.fieldName] = uniqueValues[0].value;
                    }
                }
            });

            // Convert final values to CrfReadingValue format
            const data: CrfReadingValue[] = Object.entries(finalValues).map(([fieldName, value]) => {
                const field = fields.find((f) => f.fieldName === fieldName);
                if (!field) throw new Error(`Field ${fieldName} not found`);
                return {
                    uuid: field.uuid,
                    fieldName,
                    fieldValue: value,
                };
            });

            await adjudicateReadings({
                patientId,
                visitId,
                laterality: activeTab,
                data,
            }).unwrap();

            onClose();
        } catch (error) {
            enqueueSnackbar("Failed to create adjudicated reading. Please try again.", { variant: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Check if form is ready to submit (all conflicts have a selection or custom value)
    const isReadyToSubmit = (): boolean => {
        // If there are validation errors, form is not ready
        if (Object.values(validationErrors).some((error) => error)) {
            return false;
        }

        // If any field is in edit mode, form is not ready
        if (Object.values(editMode).some((isEditing) => isEditing)) {
            return false;
        }

        for (const field of fields) {
            const fieldName = field.fieldName;
            if (hasConflict(fieldName) && !selectedValues[fieldName] && !customValues[fieldName]) {
                return false;
            }
        }
        return true;
    };

    // Get field formatter similar to the main table
    const formatFieldValue = (field: CrfField, value: string): string => {
        if (!value) return "";

        if (field.fieldType === CrfFieldType.date) {
            return new Date(value).toLocaleDateString("en-US", { timeZone: "UTC" });
        }

        if (field.fieldType === CrfFieldType.list) {
            const options = field.fieldOptions;
            // Compare values as strings to avoid type mismatches
            const selectedOption = options?.find((option: any) => option.value?.toString() === value.toString());

            if (selectedOption) {
                return selectedOption.label;
            }
        }

        // Format number with precision if specified
        if (field.precision !== undefined) {
            const isNumber = !isNaN(parseFloat(value));
            if (isNumber) {
                const formattedValue = parseFloat(value).toFixed(field.precision);
                return field.unit ? `${formattedValue} ${field.unit}` : formattedValue;
            }
        }

        // Add unit to the value if it exists and hasn't been handled by list options
        return field.unit ? `${value} ${field.unit}` : value;
    };

    const getFieldStatus = (fieldName: string) => {
        // First check if there's a custom value
        if (customValues[fieldName]) {
            return {
                label: "Custom",
                color: "primary",
            };
        }

        // Check if there's any initial value
        if (!hasValue(fieldName)) {
            return {
                label: "Empty",
                color: "warning",
            };
        }

        // Check for conflicts
        if (hasConflict(fieldName)) {
            // If there's a selected value while having conflicts, show as "Selected"
            if (selectedValues[fieldName]) {
                return {
                    label: "Selected",
                    color: "primary",
                };
            }
            return {
                label: "Conflict",
                color: "error",
            };
        }

        // If no conflicts, it must be a matched value
        return {
            label: "Matched",
            color: "success",
        };
    };

    // Render the selected tab panel content
    const renderTabContent = () => {
        return (
            <TableContainer component={Paper} sx={{ backgroundColor: "#29323f" }}>
                <Table size={isCompactRows ? "small" : "medium"}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={!isCompactRows ? { fontSize: "0.95rem", fontWeight: 500 } : {}}>
                                Field
                            </TableCell>
                            <TableCell sx={!isCompactRows ? { fontSize: "0.95rem", fontWeight: 500 } : {}}>
                                Status
                            </TableCell>
                            <TableCell sx={!isCompactRows ? { fontSize: "0.95rem", fontWeight: 500 } : {}}>
                                Values
                            </TableCell>
                            <TableCell
                                align="center"
                                width={80}
                                sx={!isCompactRows ? { fontSize: "0.95rem", fontWeight: 500 } : {}}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.map((field) => {
                            const fieldName = field.fieldName;
                            const fieldHasConflict = hasConflict(fieldName);
                            const uniqueValues = getUniqueValues(fieldName);
                            const firstValue = uniqueValues.length > 0 ? uniqueValues[0].value : "";
                            const isEditing = editMode[fieldName] === true;
                            const hasModification = isModified[fieldName] === true;

                            // If no conflict, automatically select the common value
                            if (
                                !fieldHasConflict &&
                                uniqueValues.length > 0 &&
                                selectedValues[fieldName] === undefined &&
                                customValues[fieldName] === undefined &&
                                !isEditing
                            ) {
                                handleSelectValue(fieldName, firstValue);
                            }

                            return (
                                <TableRow key={fieldName}>
                                    <TableCell sx={!isCompactRows ? { py: 2 } : {}}>
                                        <DisplayText
                                            type={isCompactRows ? "bodyXSmall" : "bodyMedium"}
                                            text={fieldName}
                                            variant="semiBold"
                                            style={isCompactRows ? { lineHeight: "1.2" } : {}}
                                        />
                                        {field.unit && (
                                            <DisplayText
                                                type={isCompactRows ? "bodyXSmall" : "bodySmall"}
                                                text={`(${field.unit})`}
                                                style={isCompactRows ? { lineHeight: "1.2" } : {}}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={!isCompactRows ? { py: 2 } : {}}>
                                        {(() => {
                                            const status = getFieldStatus(fieldName);
                                            return (
                                                <Chip
                                                    label={status.label}
                                                    color={status.color as any}
                                                    size={!isCompactRows ? "medium" : "small"}
                                                    sx={{
                                                        borderRadius: 1,
                                                        ...(!isCompactRows && { fontSize: "0.8rem" }),
                                                    }}
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell sx={!isCompactRows ? { py: 2 } : {}}>
                                        {isEditing ? (
                                            <EditField
                                                field={field}
                                                value={customValues[fieldName] || selectedValues[fieldName] || ""}
                                                onChange={(value) => handleCustomValueChange(fieldName, value)}
                                                error={validationErrors[fieldName]}
                                                autoFocus
                                            />
                                        ) : (
                                            <>
                                                {uniqueValues.map(({ value, userEmail }) => (
                                                    <Box
                                                        key={value}
                                                        display="flex"
                                                        alignItems="center"
                                                        sx={{ mb: !isCompactRows ? 1.5 : 0.3 }}
                                                    >
                                                        <Radio
                                                            checked={
                                                                selectedValues[fieldName] === value &&
                                                                !customValues[fieldName]
                                                            }
                                                            onChange={() => handleSelectValue(fieldName, value)}
                                                            size={!isCompactRows ? "medium" : "small"}
                                                        />
                                                        <Box sx={isCompactRows ? {} : { py: 4 }}>
                                                            <DisplayText
                                                                type={isCompactRows ? "bodyXSmall" : "bodyMedium"}
                                                                variant="semiBold"
                                                                text={formatFieldValue(field, value)}
                                                                style={isCompactRows ? { lineHeight: "1.2" } : {}}
                                                            />
                                                            <DisplayText
                                                                type={isCompactRows ? "bodyXSmall" : "bodyXSmall"}
                                                                text={userEmail}
                                                                style={isCompactRows ? { lineHeight: "1.2" } : {}}
                                                            />
                                                        </Box>
                                                    </Box>
                                                ))}
                                                {customValues[fieldName] && (
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        sx={{ mb: !isCompactRows ? 1.5 : 0.3 }}
                                                    >
                                                        <Radio
                                                            checked={true}
                                                            readOnly
                                                            size={!isCompactRows ? "medium" : "small"}
                                                        />
                                                        <Box>
                                                            <DisplayText
                                                                type={!isCompactRows ? "bodySmall" : "bodyXSmall"}
                                                                text={formatFieldValue(field, customValues[fieldName])}
                                                                style={isCompactRows ? { lineHeight: "1.2" } : {}}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: "text.secondary",
                                                                    display: "block",
                                                                    lineHeight: isCompactRows ? "1.1" : "1.3",
                                                                    ...(!isCompactRows && {
                                                                        fontSize: "0.8rem",
                                                                    }),
                                                                }}
                                                            >
                                                                Custom value
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={!isCompactRows ? { py: 2 } : {}}>
                                        {isEditing ? (
                                            <Box display="flex" justifyContent="center">
                                                <IconButton
                                                    size={!isCompactRows ? "medium" : "small"}
                                                    onClick={() => confirmEdit(fieldName)}
                                                    color="primary"
                                                    disabled={!hasModification || !!validationErrors[fieldName]}
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    <CheckIcon fontSize={!isCompactRows ? "medium" : "small"} />
                                                </IconButton>
                                                <IconButton
                                                    size={!isCompactRows ? "medium" : "small"}
                                                    onClick={() => cancelEditMode(fieldName)}
                                                    color="error"
                                                >
                                                    <CloseIcon fontSize={!isCompactRows ? "medium" : "small"} />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <IconButton
                                                size={!isCompactRows ? "medium" : "small"}
                                                onClick={() => startEditMode(fieldName)}
                                                color="default"
                                            >
                                                <EditIcon fontSize={!isCompactRows ? "medium" : "small"} />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Adjudicate Readings
                <DisplayText
                    type="bodySmall"
                    text="Select the final value for each field with conflicts or click the edit icon to enter a custom value. Fields without conflicts will automatically use the common value."
                />
            </DialogTitle>
            <DialogContent>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Tabs value={activeTab} onChange={handleTabChange} aria-label="laterality tabs">
                            <Tab label="OD readings" value={Laterality.OD} disabled={!canAdjudicate[Laterality.OD]} />
                            <Tab label="OS readings " value={Laterality.OS} disabled={!canAdjudicate[Laterality.OS]} />
                        </Tabs>

                        <ToggleButton
                            value="compact"
                            color="primary"
                            selected={isCompactRows}
                            onChange={() => setIsCompactRows(!isCompactRows)}
                            size="small"
                            aria-label="compact rows"
                        >
                            <CompressIcon fontSize="small" />
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                                Compact Rows
                            </Typography>
                        </ToggleButton>
                    </Box>
                </Box>

                {renderTabContent()}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between" }}>
                <Box onClick={() => window.open(window.location.href, "_blank")} sx={{ px: 16, cursor: "pointer" }}>
                    <DisplayText
                        type="bodySmall"
                        text="Open patient in new tab"
                        style={{ textDecoration: "underline", color: "#90caf9" }}
                    />
                </Box>
                <Box>
                    <Button onClick={onClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                        disabled={!isReadyToSubmit() || isSubmitting}
                    >
                        {isSubmitting ? "Creating read..." : "Create adjudication read"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default AdjudicationDialog;
